import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';
import './StudentActions.css';

function Teachers() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [error, setError] = useState(null);
  const { selectedBranchId, handleBranchChange, refreshTrigger } = useBranchFilter();
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [viewingTeacher, setViewingTeacher] = useState(null);
  const [viewingTeacherProfileUrl, setViewingTeacherProfileUrl] = useState(null);
  const [editForm, setEditForm] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    address: '',
    country: '',
    city: '',
    postal_code: '',
    national_id: '',
    employee_id: '',
    subjects: [''],
    class_ids: [],
    class_sections: {},
    experience: '',
    salary: '',
    qualification: '',
    joining_date: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    is_active: true,
    profile_picture: null,
    // Password change fields
    password: '',
    confirmPassword: '',
    branch_id: null
  });
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [editErrors, setEditErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [viewMode, setViewMode] = useState('active'); // 'active' or 'archived'
  const [showArchiveConfirmPopup, setShowArchiveConfirmPopup] = useState(false);
  const [teacherToArchive, setTeacherToArchive] = useState(null);
  const [showArchiveSuccessPopup, setShowArchiveSuccessPopup] = useState(false);
  const [archivedTeacherName, setArchivedTeacherName] = useState('');
  const [showRestoreConfirmPopup, setShowRestoreConfirmPopup] = useState(false);
  const [teacherToRestore, setTeacherToRestore] = useState(null);
  const [showRestoreSuccessPopup, setShowRestoreSuccessPopup] = useState(false);
  const [restoredTeacherName, setRestoredTeacherName] = useState('');
  const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState(null);
  const [showDeleteSuccessPopup, setShowDeleteSuccessPopup] = useState(false);
  const [deletedTeacherName, setDeletedTeacherName] = useState('');
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Country code states
  const [editPhoneCountryCode, setEditPhoneCountryCode] = useState('+254');
  const [editEmergencyPhoneCountryCode, setEditEmergencyPhoneCountryCode] = useState('+254');
  
  // Available subjects
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  
  // Branch state
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  
  // Success popup
  const [showEditSuccessPopup, setShowEditSuccessPopup] = useState(false);
  const [updatedTeacherName, setUpdatedTeacherName] = useState('');

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  const fetchTeachers = async (branchIdOverride = null) => {
    if (!schoolData?.id) return;
    
    // Get the current branchId - use override, then hook value, then localStorage
    const currentBranchId = branchIdOverride !== null 
      ? branchIdOverride 
      : (selectedBranchId !== undefined 
          ? selectedBranchId 
          : (() => {
              const saved = localStorage.getItem('selectedBranchId');
              return saved && saved !== 'null' ? parseInt(saved) : null;
            })());
    
    try {
      setLoadingTeachers(true);
      let query = supabase
        .from('teachers')
        .select('*')
        .eq('school_id', schoolData.id);
      
      // Filter by active/inactive status based on view mode
      if (viewMode === 'active') {
        query = query.eq('is_active', true);
      } else {
        query = query.eq('is_active', false);
      }
      
      // Filter by branch if one is selected
      if (currentBranchId) {
        query = query.eq('branch_id', currentBranchId);
      }
      
      const { data, error } = await query.order('first_name');
      
      if (error) {
        console.error('Error fetching teachers:', error);
        setError('Failed to load teachers');
        return;
      }
      
      setTeachers(data || []);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load teachers');
    } finally {
      setLoadingTeachers(false);
    }
  };

  useEffect(() => {
    // Fetch teachers for the school
    if (schoolData?.id) {
      fetchTeachers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolData?.id, viewMode, selectedBranchId, refreshTrigger]);

  // Listen for branch change events - force refresh when branch changes
  useEffect(() => {
    const handleBranchChange = (event) => {
      // Get branchId from event detail
      const branchId = event?.detail?.branchId !== undefined ? event.detail.branchId : null;
      
      // Small delay to ensure state has updated, then fetch with the new branchId
      setTimeout(() => {
        if (schoolData?.id) {
          fetchTeachers(branchId);
        }
      }, 50);
    };

    window.addEventListener('branchChanged', handleBranchChange);
    return () => {
      window.removeEventListener('branchChanged', handleBranchChange);
    };
  }, [schoolData?.id]);

  useEffect(() => {
    // Fetch classes for the school
    const fetchClasses = async () => {
      if (!schoolData?.id) return;
      
      try {
        setLoadingClasses(true);
        const { data, error } = await supabase
          .from('classes')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('is_active', true)
          .order('class_name');
        
        if (error) {
          console.error('Error fetching classes:', error);
          return;
        }
        
        setClasses(data || []);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoadingClasses(false);
      }
    };

    if (schoolData?.id) {
      fetchClasses();
    }
  }, [schoolData?.id]);

  // Fetch available subjects
  useEffect(() => {
    if (schoolData?.id) {
      fetchDistinctSubjects();
    }
  }, [schoolData?.id, selectedBranchId]);

  // Fetch branches when school data is available
  useEffect(() => {
    const fetchBranches = async () => {
      if (!schoolData?.id) return;
      
      try {
        setLoadingBranches(true);
        const { data, error } = await supabase
          .from('branches')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('is_active', true)
          .order('branch_name');
        
        if (error) {
          console.error('Error fetching branches:', error);
          setBranches([]);
        } else {
          setBranches(data || []);
        }
      } catch (err) {
        console.error('Error:', err);
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    if (schoolData?.id) {
      fetchBranches();
    }
  }, [schoolData?.id]);

  const fetchDistinctSubjects = async () => {
    if (!schoolData?.id) return;

    setLoadingSubjects(true);
    try {
      // Fetch all subjects for this school
      let subjectsQuery = supabase
        .from('subjects')
        .select('subject_name')
        .eq('school_id', schoolData.id);

      if (selectedBranchId) {
        subjectsQuery = subjectsQuery.eq('branch_id', selectedBranchId);
      }

      const { data: subjectsData, error: subjectsError } = await subjectsQuery;

      if (subjectsError) throw subjectsError;

      // Get distinct subject names (case-insensitive)
      const subjectNames = (subjectsData || []).map(s => s.subject_name);
      const distinctSubjects = [...new Set(subjectNames.map(name => name.toLowerCase()))]
        .map(lowerName => {
          // Find the original case version
          return subjectNames.find(name => name.toLowerCase() === lowerName);
        })
        .filter(Boolean)
        .sort();

      setAvailableSubjects(distinctSubjects);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    } finally {
      setLoadingSubjects(false);
    }
  };

  const handleArchiveTeacher = (teacher) => {
    setTeacherToArchive(teacher);
    setShowArchiveConfirmPopup(true);
  };

  const confirmArchiveTeacher = async () => {
    if (!teacherToArchive) return;

      setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('teachers')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', teacherToArchive.id);
      
      if (error) {
        console.error('Error archiving teacher:', error);
        alert('Failed to archive teacher. Please try again.');
        setIsSubmitting(false);
        setShowArchiveConfirmPopup(false);
        setTeacherToArchive(null);
        return;
      }
      
      // Store archived teacher name for success popup
      const archivedName = `${teacherToArchive.first_name} ${teacherToArchive.last_name}`;
      
      // Refresh teachers list
      await fetchTeachers();
      setShowArchiveConfirmPopup(false);
      setTeacherToArchive(null);
      
      // Show success popup
      setArchivedTeacherName(archivedName);
      setShowArchiveSuccessPopup(true);
    } catch (err) {
      console.error('Error archiving teacher:', err);
      alert('Failed to archive teacher. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelArchiveTeacher = () => {
    setShowArchiveConfirmPopup(false);
    setTeacherToArchive(null);
  };

  const handleRestoreTeacher = (teacher) => {
    setTeacherToRestore(teacher);
    setShowRestoreConfirmPopup(true);
  };

  const confirmRestoreTeacher = async () => {
    if (!teacherToRestore) return;

      setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('teachers')
        .update({ 
          is_active: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', teacherToRestore.id);
      
      if (error) {
        console.error('Error restoring teacher:', error);
        alert('Failed to restore teacher. Please try again.');
        setIsSubmitting(false);
        setShowRestoreConfirmPopup(false);
        setTeacherToRestore(null);
        return;
      }
      
      // Store restored teacher name for success popup
      const restoredName = `${teacherToRestore.first_name} ${teacherToRestore.last_name}`;
      
      // Refresh teachers list
      await fetchTeachers();
      setShowRestoreConfirmPopup(false);
      setTeacherToRestore(null);
      
      // Show success popup
      setRestoredTeacherName(restoredName);
      setShowRestoreSuccessPopup(true);
    } catch (err) {
      console.error('Error restoring teacher:', err);
      alert('Failed to restore teacher. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelRestoreTeacher = () => {
    setShowRestoreConfirmPopup(false);
    setTeacherToRestore(null);
  };

  const handleDeleteTeacher = (teacherId, teacherName, authUserId, teacherEmail) => {
    setTeacherToDelete({
      id: teacherId,
      name: teacherName,
      authUserId: authUserId,
      email: teacherEmail
    });
    setShowDeleteConfirmPopup(true);
  };

  const confirmDeleteTeacher = async () => {
    if (!teacherToDelete) return;

    try {
      setIsSubmitting(true);
      console.log('=== COMPLETE TEACHER DELETION STARTED ===');
      console.log('Teacher ID:', teacherToDelete.id);
      console.log('Teacher Name:', teacherToDelete.name);
      console.log('Auth User ID:', teacherToDelete.authUserId);

      // Step 1: Delete attendance records (if attendance table exists)
      try {
        console.log('Step 1: Deleting attendance records...');
        const { error: attendanceError } = await supabase
          .from('attendance')
          .delete()
          .eq('teacher_id', teacherToDelete.id);

        if (attendanceError) {
          console.warn('Could not delete attendance records (table may not exist):', attendanceError);
        } else {
          console.log('✅ Attendance records deleted successfully');
        }
      } catch (attendanceErr) {
        console.warn('Attendance deletion failed:', attendanceErr);
      }

      // Step 2: Delete class schedule records (if class_schedules table exists)
      try {
        console.log('Step 2: Deleting class schedule records...');
        const { error: scheduleError } = await supabase
          .from('class_schedules')
          .delete()
          .eq('teacher_id', teacherToDelete.id);

        if (scheduleError) {
          console.warn('Could not delete class schedule records (table may not exist):', scheduleError);
        } else {
          console.log('✅ Class schedule records deleted successfully');
        }
      } catch (scheduleErr) {
        console.warn('Class schedule deletion failed:', scheduleErr);
      }

      // Step 3: Update classes table to remove teacher references
      try {
        console.log('Step 3: Updating class assignments...');
        const { error: classError } = await supabase
          .from('classes')
          .update({ 
            teacher_name: 'Unassigned',
            teacher_email: null 
          })
          .eq('teacher_email', teacherToDelete.email);

        if (classError) {
          console.warn('Could not update class assignments:', classError);
        } else {
          console.log('✅ Class assignments updated successfully');
        }
      } catch (classErr) {
        console.warn('Class assignment update failed:', classErr);
      }

      // Step 4: Delete the teacher record from teachers table
      console.log('Step 4: Deleting teacher record...');
      const { error: teacherError } = await supabase
        .from('teachers')
        .delete()
        .eq('id', teacherToDelete.id);

      if (teacherError) {
        console.error('❌ Error deleting teacher record:', teacherError);
        alert('Failed to delete teacher record. Please try again.');
        setIsSubmitting(false);
        setShowDeleteConfirmPopup(false);
        setTeacherToDelete(null);
        return;
      }
      console.log('✅ Teacher record deleted successfully');

      // Step 5: Delete the user record from users table (if exists)
      if (teacherToDelete.authUserId) {
        console.log('Step 5: Deleting user record...');
        const { error: userError } = await supabase
          .from('users')
          .delete()
          .eq('id', teacherToDelete.authUserId);

        if (userError) {
          console.warn('Could not delete user record:', userError);
        } else {
          console.log('✅ User record deleted successfully');
        }
      }

      // Step 6: Delete the auth user (this might require admin privileges)
      if (teacherToDelete.authUserId) {
        console.log('Step 6: Deleting auth user...');
        try {
          const { error: authError } = await supabase.auth.admin.deleteUser(teacherToDelete.authUserId);
          if (authError) {
            console.warn('Could not delete auth user:', authError);
          } else {
            console.log('✅ Auth user deleted successfully');
          }
        } catch (authErr) {
          console.warn('Auth user deletion failed:', authErr);
        }
      }

      console.log('=== TEACHER DELETION COMPLETED ===');
      
      // Store deleted teacher name for success popup
      const deletedName = teacherToDelete.name;
      
      // Refresh the teachers list
      await fetchTeachers();
      
      // Close confirmation popup and show success popup
      setShowDeleteConfirmPopup(false);
      setTeacherToDelete(null);
      setDeletedTeacherName(deletedName);
      setShowDeleteSuccessPopup(true);
      
    } catch (err) {
      console.error('❌ Error deleting teacher:', err);
      alert('Failed to delete teacher. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelDeleteTeacher = () => {
    setShowDeleteConfirmPopup(false);
    setTeacherToDelete(null);
  };

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Export teachers data to Excel (CSV format)
  const handleExportData = () => {
    if (teachers.length === 0) {
      alert('No teachers data to export.');
      return;
    }

    try {
      // Prepare data for export
      const exportData = teachers.map((teacher, index) => {
        // Format subjects
        const subjectsList = teacher.subjects && Array.isArray(teacher.subjects) && teacher.subjects.length > 0
          ? teacher.subjects.join('; ')
          : (teacher.subjects || 'N/A');
        
        // Format classes
        let classesList = 'N/A';
        if (teacher.class_ids && Array.isArray(teacher.class_ids) && teacher.class_ids.length > 0) {
          const classNames = teacher.class_ids.map(classId => {
            const cls = classes.find(c => c.id === classId);
            return cls ? cls.class_name : `Class ${classId}`;
          });
          classesList = classNames.join('; ');
        }
        
        // Format dates
        const dob = teacher.date_of_birth ? formatDate(teacher.date_of_birth) : 'N/A';
        const joiningDate = teacher.joining_date ? formatDate(teacher.joining_date) : 'N/A';
        
        return {
          'S.No': index + 1,
          'First Name': teacher.first_name || '',
          'Middle Name': teacher.middle_name || '',
          'Last Name': teacher.last_name || '',
          'Full Name': `${teacher.first_name || ''} ${teacher.middle_name || ''} ${teacher.last_name || ''}`.trim(),
          'Email': teacher.email || 'N/A',
          'Phone': teacher.phone || 'N/A',
          'Date of Birth': dob,
          'Address': teacher.address || 'N/A',
          'Country': teacher.country || 'N/A',
          'City': teacher.city || 'N/A',
          'Postal Code': teacher.postal_code || 'N/A',
          'National ID': teacher.national_id || 'N/A',
          'Subjects': subjectsList,
          'Classes': classesList,
          'Experience': teacher.experience || 'N/A',
          'Salary': teacher.salary || 'N/A',
          'Qualification': teacher.qualification || 'N/A',
          'Joining Date': joiningDate,
          'Emergency Contact Name': teacher.emergency_contact_name || 'N/A',
          'Emergency Contact Phone': teacher.emergency_contact_phone || 'N/A',
          'Status': teacher.is_active ? 'Active' : 'Inactive'
        };
      });

      // Convert to CSV format
      const headers = Object.keys(exportData[0]);
      const csvRows = [];
      
      // Add headers
      csvRows.push(headers.join(','));
      
      // Add data rows
      exportData.forEach(row => {
        const values = headers.map(header => {
          const value = row[header];
          // Escape commas and quotes in values
          if (value === null || value === undefined) return '';
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        });
        csvRows.push(values.join(','));
      });
      
      // Create CSV content
      const csvContent = csvRows.join('\n');
      
      // Create blob and download
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel UTF-8 support
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename with current date
      const date = new Date();
      const dateStr = date.toISOString().split('T')[0];
      const filename = `teachers_export_${dateStr}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`Exported ${teachers.length} teachers to ${filename}`);
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data. Please try again.');
    }
  };

  const formatSubjects = (subjects) => {
    if (!subjects || !Array.isArray(subjects)) return 'No subjects';
    return subjects.filter(subject => subject.trim() !== '').join(', ') || 'No subjects';
  };

  const formatClasses = (classIds) => {
    if (!classIds || !Array.isArray(classIds)) return 'No classes assigned';
    return classIds.length > 0 ? `${classIds.length} class(es) assigned` : 'No classes assigned';
  };

  const getValidFileUrl = async (url) => {
    if (!url) return null;
    
    try {
      let filePath = null;
      let bucketName = null;
      
      // Check for teacher-uploads bucket first (for teacher profile pictures)
      // Pattern 1: Private URL: /storage/v1/object/teacher-uploads/path
      const teacherPattern1 = url.match(/\/storage\/v1\/object\/teacher-uploads\/(.+)$/);
      // Pattern 2: Public URL: /storage/v1/object/public/teacher-uploads/path
      const teacherPattern2 = url.match(/\/storage\/v1\/object\/public\/teacher-uploads\/(.+)$/);
      // Pattern 3: Short format: teacher-uploads/path
      const teacherPattern3 = url.match(/teacher-uploads\/(.+)$/);
      // Pattern 4: Full URL with domain: https://...supabase.co/storage/v1/object/public/teacher-uploads/path
      const teacherPattern4 = url.match(/storage\/v1\/object\/public\/teacher-uploads\/(.+)$/);
      // Pattern 5: Full URL private: https://...supabase.co/storage/v1/object/teacher-uploads/path
      const teacherPattern5 = url.match(/storage\/v1\/object\/teacher-uploads\/(.+)$/);
      
      if (teacherPattern1 || teacherPattern2 || teacherPattern3 || teacherPattern4 || teacherPattern5) {
        bucketName = 'teacher-uploads';
        filePath = decodeURIComponent((teacherPattern1?.[1] || teacherPattern2?.[1] || teacherPattern3?.[1] || teacherPattern4?.[1] || teacherPattern5?.[1]));
        console.log('✅ Matched teacher-uploads bucket, filePath:', filePath);
      } else {
        // Check for student-images bucket (for student profile pictures)
        const studentPattern1 = url.match(/\/storage\/v1\/object\/student-images\/(.+)$/);
        const studentPattern2 = url.match(/\/storage\/v1\/object\/public\/student-images\/(.+)$/);
        const studentPattern3 = url.match(/student-images\/(.+)$/);
        
        if (studentPattern1 || studentPattern2 || studentPattern3) {
          bucketName = 'student-images';
          filePath = decodeURIComponent((studentPattern1?.[1] || studentPattern2?.[1] || studentPattern3?.[1]));
        }
      }
      
      // Pattern 4: Direct path (already a path, not a URL) - remove leading slash if present
      if (!filePath && !url.includes('http') && !url.includes('://')) {
        filePath = url.startsWith('/') ? url.substring(1) : url;
        // Check which bucket prefix exists
        if (filePath.startsWith('teacher-uploads/')) {
          bucketName = 'teacher-uploads';
          filePath = filePath.substring('teacher-uploads/'.length);
        } else if (filePath.startsWith('student-images/')) {
          bucketName = 'student-images';
          filePath = filePath.substring('student-images/'.length);
        }
      }
      
      // If we found a file path and bucket, generate a signed URL
      if (filePath && bucketName) {
        console.log('Extracted file path:', filePath, 'from bucket:', bucketName);
        
        // Clean the file path - remove any query parameters
        const cleanPath = filePath.split('?')[0];
        
        try {
          const { data, error } = await supabase.storage
            .from(bucketName)
            .createSignedUrl(cleanPath, 3600); // Valid for 1 hour
          
          if (!error && data?.signedUrl) {
            console.log('Generated signed URL successfully:', data.signedUrl);
            return data.signedUrl;
          } else {
            console.error('Error creating signed URL:', error);
            // Try public URL as fallback
            const { data: publicData } = supabase.storage
              .from(bucketName)
              .getPublicUrl(cleanPath);
            if (publicData?.publicUrl) {
              console.log('Using public URL as fallback:', publicData.publicUrl);
              return publicData.publicUrl;
            }
          }
        } catch (signError) {
          console.error('Error in createSignedUrl:', signError);
          // Try public URL as fallback
          try {
            const { data: publicData } = supabase.storage
              .from(bucketName)
              .getPublicUrl(cleanPath);
            if (publicData?.publicUrl) {
              return publicData.publicUrl;
            }
          } catch (publicError) {
            console.error('Error getting public URL:', publicError);
          }
        }
      }
      
      // If all else fails, return original URL
      console.log('Using original URL:', url);
      return url;
    } catch (err) {
      console.error('Error in getValidFileUrl:', err);
      return url; // Return original URL as fallback
    }
  };

  const handleEditTeacher = async (teacher) => {
    setEditingTeacher(teacher);
    
    // Ensure class_ids are numbers for proper comparison
    const classIds = teacher.class_ids || [];
    const normalizedClassIds = classIds.map(id => typeof id === 'string' ? parseInt(id, 10) : id).filter(id => !isNaN(id));
    
    // Normalize class_sections keys to numbers
    const classSections = teacher.class_sections || {};
    const normalizedClassSections = {};
    Object.keys(classSections).forEach(key => {
      const numKey = typeof key === 'string' ? parseInt(key, 10) : key;
      if (!isNaN(numKey)) {
        normalizedClassSections[numKey] = classSections[key];
      }
    });
    
    // Extract country code from phone number
    let phoneNumber = teacher.phone || '';
    let phoneCountryCode = '+254'; // Default
    if (phoneNumber && phoneNumber.includes(' ')) {
      const parts = phoneNumber.split(' ');
      if (parts[0].startsWith('+')) {
        phoneCountryCode = parts[0];
        phoneNumber = parts.slice(1).join(' ');
      }
    }
    
    // Extract country code from emergency phone
    let emergencyPhoneNumber = teacher.emergency_contact_phone || '';
    let emergencyPhoneCountryCode = '+254'; // Default
    if (emergencyPhoneNumber && emergencyPhoneNumber.includes(' ')) {
      const parts = emergencyPhoneNumber.split(' ');
      if (parts[0].startsWith('+')) {
        emergencyPhoneCountryCode = parts[0];
        emergencyPhoneNumber = parts.slice(1).join(' ');
      }
    }
    
    setEditPhoneCountryCode(phoneCountryCode);
    setEditEmergencyPhoneCountryCode(emergencyPhoneCountryCode);
    
    setEditForm({
      first_name: teacher.first_name || '',
      middle_name: teacher.middle_name || '',
      last_name: teacher.last_name || '',
      email: teacher.email || '',
      phone: phoneNumber,
      date_of_birth: teacher.date_of_birth || '',
      address: teacher.address || '',
      country: teacher.country || 'Kenya',
      city: teacher.city || '',
      postal_code: teacher.postal_code || '',
      national_id: teacher.national_id || '',
      employee_id: teacher.employee_id || '',
      subjects: teacher.subjects && teacher.subjects.length > 0 ? teacher.subjects : [''],
      class_ids: normalizedClassIds,
      class_sections: normalizedClassSections,
      experience: teacher.experience || '',
      salary: teacher.salary || '',
      qualification: teacher.qualification || '',
      joining_date: teacher.joining_date || '',
      emergency_contact_name: teacher.emergency_contact_name || '',
      emergency_contact_phone: emergencyPhoneNumber,
      is_active: teacher.is_active !== false,
      profile_picture: null,
      branch_id: teacher.branch_id || null,
      // Password fields - leave empty for security
      password: '',
      confirmPassword: ''
    });
    
    // Set profile picture preview if exists - generate signed URL immediately
    if (teacher.profile_picture_url) {
      console.log('=== TEACHER PROFILE PICTURE DEBUG ===');
      console.log('Teacher has profile picture URL:', teacher.profile_picture_url);
      console.log('Teacher object:', teacher);
      
      // Generate signed URL immediately (don't wait)
      getValidFileUrl(teacher.profile_picture_url)
        .then(signedUrl => {
          console.log('✅ Generated signed URL:', signedUrl);
          if (signedUrl) {
            setProfilePicturePreview(signedUrl);
            console.log('✅ Set profilePicturePreview to signed URL');
          } else {
            // Fallback to original URL
            setProfilePicturePreview(teacher.profile_picture_url);
            console.log('⚠️ Using original URL as fallback');
          }
        })
        .catch(err => {
          console.error('❌ Error generating profile picture URL for edit:', err);
          // Fallback to original URL
          setProfilePicturePreview(teacher.profile_picture_url);
          console.log('⚠️ Using original URL due to error');
        });
    } else {
      console.log('❌ Teacher has no profile picture URL');
      setProfilePicturePreview(null);
    }
    setEditErrors({});
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle branch_id - convert to number or null
    let processedValue = value;
    if (name === 'branch_id') {
      processedValue = value === '' ? null : parseInt(value, 10);
    } else {
      processedValue = type === 'checkbox' ? checked : value;
    }
    
    setEditForm(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Clear error for this field
    if (editErrors[name]) {
      setEditErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleEditSubjectChange = (index, value) => {
    const newSubjects = [...editForm.subjects];
    newSubjects[index] = value;
    setEditForm(prev => ({
      ...prev,
      subjects: newSubjects
    }));
  };

  const addEditSubject = () => {
    setEditForm(prev => ({
      ...prev,
      subjects: [...prev.subjects, '']
    }));
  };

  const removeEditSubject = (index) => {
    if (editForm.subjects.length > 1) {
      const newSubjects = editForm.subjects.filter((_, i) => i !== index);
      setEditForm(prev => ({
        ...prev,
        subjects: newSubjects
      }));
    }
  };

  const handleEditClassChange = (classId) => {
    // Ensure classId is a number for consistent comparison
    const numClassId = typeof classId === 'string' ? parseInt(classId, 10) : classId;
    
    // Normalize existing class_ids to numbers for comparison
    const normalizedClassIds = editForm.class_ids.map(id => typeof id === 'string' ? parseInt(id, 10) : id);
    
    const newClassIds = normalizedClassIds.includes(numClassId)
      ? normalizedClassIds.filter(id => id !== numClassId)
      : [...normalizedClassIds, numClassId];
    
    setEditForm(prev => {
      const newClassSections = { ...prev.class_sections };
      
      // Normalize keys in class_sections
      const normalizedSections = {};
      Object.keys(newClassSections).forEach(key => {
        const numKey = typeof key === 'string' ? parseInt(key, 10) : key;
        if (!isNaN(numKey)) {
          normalizedSections[numKey] = newClassSections[key];
        }
      });
      
      if (newClassIds.includes(numClassId)) {
        // If class is selected, ensure it has an empty array for sections
        normalizedSections[numClassId] = normalizedSections[numClassId] || [];
      } else {
        // If class is deselected, remove it from class_sections
        delete normalizedSections[numClassId];
      }
      
      return {
        ...prev,
        class_ids: newClassIds,
        class_sections: normalizedSections
      };
    });
  };

  const handleEditClassSectionChange = (classId, section, isChecked) => {
    // Ensure classId is a number for consistent comparison
    const numClassId = typeof classId === 'string' ? parseInt(classId, 10) : classId;
    
    setEditForm(prev => {
      // Normalize class_sections keys
      const normalizedSections = {};
      Object.keys(prev.class_sections).forEach(key => {
        const numKey = typeof key === 'string' ? parseInt(key, 10) : key;
        if (!isNaN(numKey)) {
          normalizedSections[numKey] = prev.class_sections[key];
        }
      });
      
      const currentSections = normalizedSections[numClassId] || [];
      const newSections = isChecked 
        ? [...currentSections, section]
        : currentSections.filter(s => s !== section);
      
      return {
        ...prev,
        class_sections: {
          ...normalizedSections,
          [numClassId]: newSections
        }
      };
    });
  };

  const validateEditForm = () => {
    const errors = {};
    
    if (!editForm.first_name.trim()) errors.first_name = 'First name is required';
    if (!editForm.last_name.trim()) errors.last_name = 'Last name is required';
    if (!editForm.email.trim()) errors.email = 'Email is required';
    if (!editForm.phone.trim()) errors.phone = 'Phone is required';
    
    // Validate subjects
    const validSubjects = editForm.subjects.filter(subject => subject.trim() !== '');
    if (validSubjects.length === 0) {
      errors.subjects = 'At least one subject is required';
    }
    
    // Check for duplicate subjects
    const subjectSet = new Set();
    editForm.subjects.forEach((subject, index) => {
      if (subject.trim() !== '') {
        if (subjectSet.has(subject.trim().toLowerCase())) {
          errors[`subject_${index}`] = 'Duplicate subject';
        } else {
          subjectSet.add(subject.trim().toLowerCase());
        }
      }
    });
    
    if (validSubjects.length === 0) {
      errors.subjects = 'At least one subject is required';
    }
    
    // Password validation (only if passwords are provided)
    // No length restriction
    
    if (editForm.password && editForm.password !== editForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const safeTrim = (value) => {
    if (typeof value === 'string') {
      return value.trim();
    }
    return value;
  };

  const safeStringCheck = (value) => {
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    return value !== '' && value != null;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateEditForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Handle profile picture upload if a new one is selected
      let profilePictureUrl = editingTeacher.profile_picture_url || null;
      if (editForm.profile_picture) {
        try {
          setUploadingPicture(true);
          const fileExt = editForm.profile_picture.name.split('.').pop();
          const fileName = `teacher_${editingTeacher.id}_${Date.now()}.${fileExt}`;
          const filePath = `teacher-profiles/${schoolData.id}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('teacher-uploads')
            .upload(filePath, editForm.profile_picture, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            console.error('Error uploading profile picture:', uploadError);
            alert('Failed to upload profile picture. Please try again.');
            setUploadingPicture(false);
            return;
          }

          const { data: { publicUrl } } = supabase.storage
            .from('teacher-uploads')
            .getPublicUrl(filePath);
          
          profilePictureUrl = publicUrl;
          setUploadingPicture(false);
        } catch (uploadErr) {
          console.error('Error uploading profile picture:', uploadErr);
          alert('Failed to upload profile picture. Please try again.');
          setUploadingPicture(false);
          return;
        }
      }

      // Clean and prepare the update data
      const phoneWithCode = editForm.phone.trim() ? `${editPhoneCountryCode} ${editForm.phone.trim()}` : '';
      const emergencyPhoneWithCode = editForm.emergency_contact_phone.trim() ? `${editEmergencyPhoneCountryCode} ${editForm.emergency_contact_phone.trim()}` : '';
      
      const updateData = {
        branch_id: editForm.branch_id || null, // Use branch_id from form
        first_name: safeTrim(editForm.first_name),
        middle_name: safeStringCheck(editForm.middle_name) ? safeTrim(editForm.middle_name) : null,
        last_name: safeTrim(editForm.last_name),
        email: safeTrim(editForm.email),
        phone: phoneWithCode,
        date_of_birth: safeStringCheck(editForm.date_of_birth) ? editForm.date_of_birth : null,
        address: safeStringCheck(editForm.address) ? safeTrim(editForm.address) : null,
        country: safeStringCheck(editForm.country) ? safeTrim(editForm.country) : null,
        city: safeStringCheck(editForm.city) ? safeTrim(editForm.city) : null,
        postal_code: safeStringCheck(editForm.postal_code) ? safeTrim(editForm.postal_code) : null,
        national_id: safeStringCheck(editForm.national_id) ? safeTrim(editForm.national_id) : null,
        employee_id: safeStringCheck(editForm.employee_id) ? safeTrim(editForm.employee_id) : null,
        subjects: editForm.subjects.filter(subject => safeStringCheck(subject)),
        class_ids: editForm.class_ids,
        class_sections: editForm.class_sections,
        experience: safeStringCheck(editForm.experience) ? parseFloat(editForm.experience) : null,
        salary: safeStringCheck(editForm.salary) ? parseFloat(editForm.salary) : null,
        qualification: safeStringCheck(editForm.qualification) ? safeTrim(editForm.qualification) : null,
        joining_date: safeStringCheck(editForm.joining_date) ? editForm.joining_date : null,
        emergency_contact_name: safeStringCheck(editForm.emergency_contact_name) ? safeTrim(editForm.emergency_contact_name) : null,
        emergency_contact_phone: emergencyPhoneWithCode,
        profile_picture_url: profilePictureUrl,
        is_active: editForm.is_active,
        updated_at: new Date().toISOString()
      };
      
      // Update password_hash if password is provided
      if (editForm.password && editForm.password !== '') {
        const passwordHash = btoa(editForm.password.trim()); // Base64 encoding (same as schools)
        updateData.password_hash = passwordHash;
        console.log('Password hash will be updated in teachers table');
      }
      
      console.log('Updating teacher with data:', updateData);
      
      const { error } = await supabase
        .from('teachers')
        .update(updateData)
        .eq('id', editingTeacher.id);
      
      if (error) {
        console.error('Error updating teacher:', error);
        alert(`Failed to update teacher: ${error.message || 'Please check your data and try again.'}`);
        return;
      }
      
      console.log('Teacher updated successfully');
      
      if (editForm.password && editForm.password !== '') {
        console.log('Teacher password updated successfully in teachers table');
      }
      
      // Refresh teachers list to get updated data
      await fetchTeachers();
      
      // Store updated teacher name for success popup
      const updatedName = `${editForm.first_name} ${editForm.last_name}`;
      setUpdatedTeacherName(updatedName);
      
      // Close popup
      setEditingTeacher(null);
      setEditForm({
        first_name: '',
        middle_name: '',
        last_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        address: '',
        country: '',
        city: '',
        postal_code: '',
        national_id: '',
        subjects: [''],
        class_ids: [],
        class_sections: {},
        experience: '',
        salary: '',
        qualification: '',
        joining_date: '',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        is_active: true,
        profile_picture: null,
        branch_id: null,
        password: '',
        confirmPassword: ''
      });
      setProfilePicturePreview(null);
      setEditErrors({});
      
      // Show success popup
      setShowEditSuccessPopup(true);
      
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to update teacher. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeEditPopup = () => {
    setEditingTeacher(null);
    setEditForm({
      first_name: '',
      middle_name: '',
      last_name: '',
      email: '',
      phone: '',
      date_of_birth: '',
      address: '',
      country: '',
      city: '',
      postal_code: '',
      national_id: '',
      subjects: [''],
      class_ids: [],
      class_sections: {},
      experience: '',
      salary: '',
      qualification: '',
      joining_date: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      is_active: true,
      profile_picture: null,
      branch_id: null,
      password: '',
      confirmPassword: ''
    });
    setProfilePicturePreview(null);
    setEditErrors({});
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      setEditForm(prev => ({
        ...prev,
        profile_picture: file
      }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeProfilePicture = () => {
    setEditForm(prev => ({
      ...prev,
      profile_picture: null
    }));
    setProfilePicturePreview(editingTeacher?.profile_picture_url || null);
  };

  const handleViewTeacher = async (teacher) => {
    setViewingTeacher(teacher);
    
    // Generate signed URL for profile picture if exists
    if (teacher.profile_picture_url) {
      try {
        const signedUrl = await getValidFileUrl(teacher.profile_picture_url);
        setViewingTeacherProfileUrl(signedUrl || teacher.profile_picture_url);
      } catch (err) {
        console.error('Error generating profile picture URL for view:', err);
        setViewingTeacherProfileUrl(teacher.profile_picture_url);
      }
    } else {
      setViewingTeacherProfileUrl(null);
    }
  };

  const closeViewPopup = () => {
    setViewingTeacher(null);
    setViewingTeacherProfileUrl(null);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">⏳</div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">❌</div>
        <p>School data not found. Please login again.</p>
        <button onClick={() => navigate('/schoollogin')} className="submit-btn">
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />

      {/* Main Content */}
      <div className="main-content">
        <div className="students-content">
          <div className="students-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h1>Teachers Management</h1>
                <p>{viewMode === 'active' ? 'View and manage all active teachers in your school' : 'View and manage archived teachers'}</p>
              </div>
            </div>
          </div>

          <div className="students-stats">
            <div className="stat-item">
              <span className="stat-number">{teachers.length}</span>
              <span className="stat-label">Total Teachers</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {teachers.reduce((total, teacher) => total + (teacher.subjects?.length || 0), 0)}
              </span>
              <span className="stat-label">Total Subjects</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {teachers.reduce((total, teacher) => total + (teacher.class_ids?.length || 0), 0)}
              </span>
              <span className="stat-label">Class Assignments</span>
            </div>
          </div>

          <div className="students-actions" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <button 
              className="add-student-btn"
              onClick={() => navigate('/school/add-teacher')}
            >
              ➕ Add New Teacher
            </button>
            <button 
              className="export-data-btn"
              onClick={handleExportData}
              disabled={teachers.length === 0}
              style={{
                padding: '0.875rem 1.5rem',
                background: teachers.length === 0 ? '#D1D5DB' : '#10B981',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: teachers.length === 0 ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: teachers.length === 0 ? 'none' : '0 2px 4px rgba(16, 185, 129, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
              onMouseEnter={(e) => {
                if (teachers.length > 0) {
                  e.currentTarget.style.background = '#059669';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (teachers.length > 0) {
                  e.currentTarget.style.background = '#10B981';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.2)';
                }
              }}
            >
              📊 Export Data
            </button>
            {/* View Mode Toggle */}
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', background: '#F3F4F6', borderRadius: '8px', padding: '0.25rem', width: 'fit-content', marginBottom: '1.5rem' }}>
              <button
                onClick={() => setViewMode('active')}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: viewMode === 'active' ? '#FFFFFF' : 'transparent',
                  color: viewMode === 'active' ? '#1F2937' : '#6B7280',
                  fontWeight: viewMode === 'active' ? '600' : '400',
                  cursor: 'pointer',
                  boxShadow: viewMode === 'active' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                👥 Active Teachers
              </button>
              <button
                onClick={() => setViewMode('archived')}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: viewMode === 'archived' ? '#FFFFFF' : 'transparent',
                  color: viewMode === 'archived' ? '#1F2937' : '#6B7280',
                  fontWeight: viewMode === 'archived' ? '600' : '400',
                  cursor: 'pointer',
                  boxShadow: viewMode === 'archived' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                📦 Archived Teachers
              </button>
            </div>
          </div>

          {loadingTeachers ? (
            <div className="loading-container">
              <div className="loading-spinner">⏳</div>
              <p>Loading teachers...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <div className="error-icon">❌</div>
              <p>{error}</p>
              <button 
                className="retry-btn"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          ) : teachers.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">👨‍🏫</div>
              <h3>No {viewMode === 'active' ? 'Active' : 'Archived'} Teachers Found</h3>
              <p>
                {viewMode === 'active' 
                  ? "You haven't added any active teachers yet. Start by adding your first teacher!"
                  : "You don't have any archived teachers yet."}
              </p>
              {viewMode === 'active' && (
                <button 
                  className="add-first-teacher-btn"
                  onClick={() => navigate('/school/add-teacher')}
                >
                  ➕ Add Your First Teacher
                </button>
              )}
            </div>
          ) : (
            <div className="students-table-container">
              <table className="students-table" style={{ tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ width: '25%' }}>Teacher Name</th>
                    <th style={{ width: '30%' }}>Email</th>
                    <th style={{ width: '30%' }}>Subjects</th>
                    <th style={{ width: '15%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teachers.map((teacher) => (
                    <tr key={teacher.id}>
                      <td className="teacher-name" style={{ color: '#000000' }}>
                        <strong>{teacher.first_name} {teacher.last_name}</strong>
                      </td>
                      <td className="email-cell">
                        <a href={`mailto:${teacher.email}`} className="email-link">
                          {teacher.email}
                        </a>
                      </td>
                      <td className="subjects-cell" style={{ wordWrap: 'break-word', overflow: 'hidden' }}>
                        <div className="subjects-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {teacher.subjects && teacher.subjects.length > 0 ? (
                            teacher.subjects.map((subject, index) => (
                              <span key={index} className="subject-tag" style={{ whiteSpace: 'nowrap' }}>
                                {subject}
                              </span>
                            ))
                          ) : (
                            <span className="no-data">No subjects</span>
                          )}
                        </div>
                      </td>
                      <td className="actions-cell students-actions-container">
                        <div className="action-buttons students-action-buttons">
                          {viewMode === 'active' ? (
                            <>
                              <button 
                                className="students-action-btn view-btn"
                                title="View Details"
                                onClick={() => handleViewTeacher(teacher)}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                  <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                                <span>View</span>
                              </button>
                              <button 
                                className="students-action-btn edit-btn"
                                title="Edit Teacher"
                                onClick={() => handleEditTeacher(teacher)}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                                <span>Edit</span>
                              </button>
                              <button 
                                className="students-action-btn archive-btn"
                                title="Archive Teacher"
                                onClick={() => handleArchiveTeacher(teacher)}
                                disabled={isSubmitting}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                                  <line x1="12" y1="22.08" x2="12" y2="12"></line>
                                </svg>
                                <span>Archive</span>
                              </button>
                              <button 
                                className="students-action-btn delete-btn"
                                title="Delete Teacher"
                                onClick={() => handleDeleteTeacher(teacher.id, `${teacher.first_name} ${teacher.last_name}`, teacher.auth_user_id, teacher.email)}
                                disabled={isSubmitting}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"></polyline>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                  <line x1="10" y1="11" x2="10" y2="17"></line>
                                  <line x1="14" y1="11" x2="14" y2="17"></line>
                                </svg>
                                <span>Delete</span>
                              </button>
                            </>
                          ) : (
                            <>
                              <button 
                                className="students-action-btn restore-btn"
                                title="Restore Teacher"
                                onClick={() => handleRestoreTeacher(teacher)}
                                disabled={isSubmitting}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                                </svg>
                                <span>Restore</span>
                              </button>
                              <button 
                                className="students-action-btn delete-btn"
                                title="Delete Teacher"
                                onClick={() => handleDeleteTeacher(teacher.id, `${teacher.first_name} ${teacher.last_name}`, teacher.auth_user_id, teacher.email)}
                                disabled={isSubmitting}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"></polyline>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                  <line x1="10" y1="11" x2="10" y2="17"></line>
                                  <line x1="14" y1="11" x2="14" y2="17"></line>
                                </svg>
                                <span>Delete</span>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit Teacher Popup */}
      {editingTeacher && (
        <div className="popup-overlay">
          <div className="popup-content">
            <div className="popup-header">
              <h2>Edit Teacher</h2>
              <button className="close-btn" onClick={closeEditPopup}>×</button>
            </div>
            
            <form onSubmit={handleEditSubmit} className="edit-teacher-form">
              <div className="form-section">
                <h3>Teacher Information</h3>
                {/* Branch Selection */}
                {branches.length > 0 && (
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="branch_id">Branch</label>
                      <select
                        id="branch_id"
                        name="branch_id"
                        value={editForm.branch_id || ''}
                        onChange={handleEditInputChange}
                        className={editErrors.branch_id ? 'error' : ''}
                      >
                        <option value="">-- Select Branch (Optional) --</option>
                        {branches.map((branch) => (
                          <option key={branch.id} value={branch.id}>
                            {branch.branch_name}
                          </option>
                        ))}
                      </select>
                      {editErrors.branch_id && (
                        <span className="error-text">{editErrors.branch_id}</span>
                      )}
                      <small className="form-help">Select the branch this teacher is assigned to. Leave empty if assigned to all branches.</small>
                    </div>
                  </div>
                )}
                <div className="form-row three-columns">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={editForm.first_name}
                      onChange={handleEditInputChange}
                      className={editErrors.first_name ? 'error' : ''}
                    />
                    {editErrors.first_name && <span className="error-text">{editErrors.first_name}</span>}
                  </div>
                  <div className="form-group">
                    <label>Middle Name</label>
                    <input
                      type="text"
                      name="middle_name"
                      value={editForm.middle_name}
                      onChange={handleEditInputChange}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={editForm.last_name}
                      onChange={handleEditInputChange}
                      className={editErrors.last_name ? 'error' : ''}
                    />
                    {editErrors.last_name && <span className="error-text">{editErrors.last_name}</span>}
                  </div>
                </div>
                
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'start' }}>
                  <div className="form-group" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                      Profile Picture
                    </label>
                    <div style={{
                      position: 'relative',
                      marginTop: '0.5rem',
                      width: '100%'
                    }}>
                      <input
                        type="file"
                        id="editProfilePicture"
                        name="profile_picture"
                        accept="image/*"
                        onChange={handleProfilePictureChange}
                        style={{
                          position: 'absolute',
                          width: '0.1px',
                          height: '0.1px',
                          opacity: 0,
                          overflow: 'hidden',
                          zIndex: -1
                        }}
                      />
                      <label
                        htmlFor="editProfilePicture"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0.5rem 1rem',
                          background: '#FFFFFF',
                          color: '#374151',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '400',
                          transition: 'all 0.2s ease',
                          width: '100%',
                          textAlign: 'center',
                          gap: '0.5rem',
                          userSelect: 'none',
                          boxSizing: 'border-box'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#F9FAFB';
                          e.currentTarget.style.borderColor = '#9CA3AF';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#FFFFFF';
                          e.currentTarget.style.borderColor = '#D1D5DB';
                        }}
                      >
                        <span style={{ fontSize: '0.9rem' }}>📷</span>
                        <span>{editForm.profile_picture ? 'Change Image' : 'Choose Image'}</span>
                      </label>
                    </div>
                    {/* Show preview section if there's a profile picture URL or new file selected */}
                    {editingTeacher && (editingTeacher.profile_picture_url || editForm.profile_picture || profilePicturePreview) && (
                      <div style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        background: '#F9FAFB',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}>
                        <div style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          border: '3px solid #FF8C42',
                          flexShrink: 0,
                          background: '#E5E7EB',
                          position: 'relative'
                        }}>
                          {(profilePicturePreview || editingTeacher.profile_picture_url) ? (
                            <img 
                              key={`${editingTeacher.id}-${profilePicturePreview || editingTeacher.profile_picture_url || 'new'}`} 
                              src={profilePicturePreview || editingTeacher.profile_picture_url} 
                              alt="Profile preview" 
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                                minHeight: '80px',
                                position: 'absolute',
                                top: 0,
                                left: 0
                              }}
                              onLoad={(e) => {
                                console.log('✅ Profile picture loaded successfully:', e.target.src);
                                e.target.style.display = 'block';
                              }}
                              onError={async (e) => {
                                console.error('❌ PROFILE PICTURE LOAD ERROR');
                                console.error('Failed URL:', e.target.src);
                                console.error('profilePicturePreview:', profilePicturePreview);
                                console.error('editingTeacher.profile_picture_url:', editingTeacher?.profile_picture_url);
                                
                                // If we have a new file preview, don't try to fallback
                                if (profilePicturePreview && profilePicturePreview.startsWith('data:')) {
                                  console.log('✅ Using data URL preview');
                                  e.target.src = profilePicturePreview;
                                  return;
                                }
                                
                                if (editingTeacher?.profile_picture_url && e.target.src !== editingTeacher.profile_picture_url) {
                                  // Try original URL first
                                  console.log('🔄 Trying original URL...');
                                  e.target.src = editingTeacher.profile_picture_url;
                                } else if (editingTeacher?.profile_picture_url) {
                                  // Try to regenerate signed URL
                                  try {
                                    console.log('🔄 Attempting to regenerate signed URL...');
                                    const newUrl = await getValidFileUrl(editingTeacher.profile_picture_url);
                                    console.log('✅ New URL generated:', newUrl);
                                    if (newUrl && newUrl !== e.target.src) {
                                      e.target.src = newUrl;
                                      console.log('✅ Updated image src to:', newUrl);
                                    } else {
                                      console.error('❌ Could not generate valid URL, showing placeholder');
                                      e.target.style.display = 'none';
                                      if (e.target.nextSibling) {
                                        e.target.nextSibling.style.display = 'flex';
                                      }
                                    }
                                  } catch (err) {
                                    console.error('❌ Error regenerating URL:', err);
                                    e.target.style.display = 'none';
                                    if (e.target.nextSibling) {
                                      e.target.nextSibling.style.display = 'flex';
                                    }
                                  }
                                } else {
                                  console.error('❌ No profile picture URL available');
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'flex';
                                  }
                                }
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              background: '#E5E7EB',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '2rem'
                            }}>
                              👤
                            </div>
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          {editForm.profile_picture ? (
                            <>
                              <div style={{ 
                                fontWeight: '600', 
                                color: '#1F2937', 
                                marginBottom: '0.25rem', 
                                wordBreak: 'break-word',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {editForm.profile_picture.name}
                              </div>
                              <div style={{ fontSize: '0.85rem', color: '#6B7280', whiteSpace: 'nowrap' }}>
                                {(editForm.profile_picture.size / 1024 / 1024).toFixed(2)} MB
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#10B981', marginTop: '0.25rem', fontWeight: '500' }}>
                                ✓ New image selected
                              </div>
                            </>
                          ) : editingTeacher.profile_picture_url ? (
                            <div style={{ fontSize: '0.85rem', color: '#6B7280' }}>
                              Current profile picture
                            </div>
                          ) : null}
                        </div>
                        {editForm.profile_picture && (
                          <button
                            type="button"
                            onClick={removeProfilePicture}
                            style={{
                              padding: '0.4rem 0.6rem',
                              background: '#EF4444',
                              color: '#FFFFFF',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              fontWeight: '500',
                              transition: 'all 0.2s ease',
                              flexShrink: 0,
                              whiteSpace: 'nowrap',
                              width: 'auto',
                              height: 'auto',
                              lineHeight: '1.2'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#DC2626';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#EF4444';
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )}
                    <small style={{ 
                      display: 'block', 
                      marginTop: '0.5rem', 
                      color: '#6B7280', 
                      fontSize: '0.85rem',
                      fontStyle: 'italic'
                    }}>
                      Max size: 5MB. Accepted formats: JPG, PNG, GIF
                    </small>
                  </div>
                  
                  <div className="form-group" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <label htmlFor="editNationalId" style={{ marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                      National/Government ID
                    </label>
                    <input
                      type="text"
                      id="editNationalId"
                      name="national_id"
                      value={editForm.national_id}
                      onChange={handleEditInputChange}
                      placeholder="Enter National/Government ID (optional)"
                      style={{
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <label htmlFor="editEmployeeId" style={{ marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                    Employee ID
                  </label>
                  <input
                    type="text"
                    id="editEmployeeId"
                    name="employee_id"
                    value={editForm.employee_id}
                    onChange={handleEditInputChange}
                    placeholder="Enter Employee ID (optional)"
                    style={{
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={editForm.email}
                      onChange={handleEditInputChange}
                      className={editErrors.email ? 'error' : ''}
                    />
                    {editErrors.email && <span className="error-text">{editErrors.email}</span>}
                  </div>
                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      name="date_of_birth"
                      value={editForm.date_of_birth}
                      onChange={handleEditInputChange}
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone *</label>
                    <div className="phone-input-wrapper">
                      <select
                        className="country-code-select"
                        value={editPhoneCountryCode}
                        onChange={(e) => setEditPhoneCountryCode(e.target.value)}
                      >
                        <option value="+1">🇺🇸 +1 (US/CA)</option>
                        <option value="+44">🇬🇧 +44 (UK)</option>
                        <option value="+91">🇮🇳 +91 (India)</option>
                        <option value="+92">🇵🇰 +92 (Pakistan)</option>
                        <option value="+971">🇦🇪 +971 (UAE)</option>
                        <option value="+966">🇸🇦 +966 (Saudi Arabia)</option>
                        <option value="+974">🇶🇦 +974 (Qatar)</option>
                        <option value="+965">🇰🇼 +965 (Kuwait)</option>
                        <option value="+973">🇧🇭 +973 (Bahrain)</option>
                        <option value="+968">🇴🇲 +968 (Oman)</option>
                        <option value="+961">🇱🇧 +961 (Lebanon)</option>
                        <option value="+962">🇯🇴 +962 (Jordan)</option>
                        <option value="+20">🇪🇬 +20 (Egypt)</option>
                        <option value="+27">🇿🇦 +27 (South Africa)</option>
                        <option value="+61">🇦🇺 +61 (Australia)</option>
                        <option value="+64">🇳🇿 +64 (New Zealand)</option>
                        <option value="+86">🇨🇳 +86 (China)</option>
                        <option value="+81">🇯🇵 +81 (Japan)</option>
                        <option value="+82">🇰🇷 +82 (South Korea)</option>
                        <option value="+65">🇸🇬 +65 (Singapore)</option>
                        <option value="+60">🇲🇾 +60 (Malaysia)</option>
                        <option value="+66">🇹🇭 +66 (Thailand)</option>
                        <option value="+62">🇮🇩 +62 (Indonesia)</option>
                        <option value="+63">🇵🇭 +63 (Philippines)</option>
                        <option value="+84">🇻🇳 +84 (Vietnam)</option>
                        <option value="+880">🇧🇩 +880 (Bangladesh)</option>
                        <option value="+94">🇱🇰 +94 (Sri Lanka)</option>
                        <option value="+977">🇳🇵 +977 (Nepal)</option>
                        <option value="+33">🇫🇷 +33 (France)</option>
                        <option value="+49">🇩🇪 +49 (Germany)</option>
                        <option value="+39">🇮🇹 +39 (Italy)</option>
                        <option value="+34">🇪🇸 +34 (Spain)</option>
                        <option value="+31">🇳🇱 +31 (Netherlands)</option>
                        <option value="+32">🇧🇪 +32 (Belgium)</option>
                        <option value="+41">🇨🇭 +41 (Switzerland)</option>
                        <option value="+43">🇦🇹 +43 (Austria)</option>
                        <option value="+46">🇸🇪 +46 (Sweden)</option>
                        <option value="+47">🇳🇴 +47 (Norway)</option>
                        <option value="+45">🇩🇰 +45 (Denmark)</option>
                        <option value="+358">🇫🇮 +358 (Finland)</option>
                        <option value="+7">🇷🇺 +7 (Russia)</option>
                        <option value="+90">🇹🇷 +90 (Turkey)</option>
                        <option value="+52">🇲🇽 +52 (Mexico)</option>
                        <option value="+55">🇧🇷 +55 (Brazil)</option>
                        <option value="+54">🇦🇷 +54 (Argentina)</option>
                        <option value="+57">🇨🇴 +57 (Colombia)</option>
                        <option value="+234">🇳🇬 +234 (Nigeria)</option>
                        <option value="+254">🇰🇪 +254 (Kenya)</option>
                        <option value="+212">🇲🇦 +212 (Morocco)</option>
                        <option value="+972">🇮🇱 +972 (Israel)</option>
                        <option value="+98">🇮🇷 +98 (Iran)</option>
                        <option value="+964">🇮🇶 +964 (Iraq)</option>
                        <option value="+967">🇾🇪 +967 (Yemen)</option>
                        <option value="+963">🇸🇾 +963 (Syria)</option>
                      </select>
                      <input
                        type="tel"
                        name="phone"
                        value={editForm.phone}
                        onChange={handleEditInputChange}
                        className={editErrors.phone ? 'error' : ''}
                        placeholder="e.g. 300 1234567"
                      />
                    </div>
                    {editErrors.phone && <span className="error-text">{editErrors.phone}</span>}
                  </div>
                  <div className="form-group">
                    <label>Address *</label>
                    <input
                      type="text"
                      name="address"
                      value={editForm.address}
                      onChange={handleEditInputChange}
                      className={editErrors.address ? 'error' : ''}
                      placeholder="Enter street address"
                    />
                    {editErrors.address && <span className="error-text">{editErrors.address}</span>}
                  </div>
                </div>
                
                <div className="form-row three-columns">
                  <div className="form-group">
                    <label>Country *</label>
                    <input
                      type="text"
                      name="country"
                      value={editForm.country}
                      onChange={handleEditInputChange}
                      className={editErrors.country ? 'error' : ''}
                      placeholder="Enter country"
                    />
                    {editErrors.country && <span className="error-text">{editErrors.country}</span>}
                  </div>
                  <div className="form-group">
                    <label>City / Town *</label>
                    <input
                      type="text"
                      name="city"
                      value={editForm.city}
                      onChange={handleEditInputChange}
                      className={editErrors.city ? 'error' : ''}
                      placeholder="Enter city or town"
                    />
                    {editErrors.city && <span className="error-text">{editErrors.city}</span>}
                  </div>
                  <div className="form-group">
                    <label>Postal Code *</label>
                    <input
                      type="text"
                      name="postal_code"
                      value={editForm.postal_code}
                      onChange={handleEditInputChange}
                      className={editErrors.postal_code ? 'error' : ''}
                      placeholder="Enter postal code"
                    />
                    {editErrors.postal_code && <span className="error-text">{editErrors.postal_code}</span>}
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Professional Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Experience (years)</label>
                    <input
                      type="number"
                      name="experience"
                      value={editForm.experience}
                      onChange={handleEditInputChange}
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Salary ($)</label>
                    <input
                      type="number"
                      name="salary"
                      value={editForm.salary}
                      onChange={handleEditInputChange}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Joining Date</label>
                  <input
                    type="date"
                    name="joining_date"
                    value={editForm.joining_date}
                    onChange={handleEditInputChange}
                  />
                </div>
                
                <div className="form-group">
                  <label>Qualification</label>
                  <input
                    type="text"
                    name="qualification"
                    value={editForm.qualification}
                    onChange={handleEditInputChange}
                    placeholder="e.g., B.Ed, M.Ed, PhD"
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Subjects *</h3>
                <div className="subjects-header" style={{
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: '1rem',
                  padding: '1rem',
                  overflowX: 'visible'
                }}>
                  <h4>Subjects *</h4>
                  <button 
                    type="button" 
                    className="add-subject-btn"
                    onClick={addEditSubject}
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      padding: '0.75rem 1rem',
                      fontSize: '0.95rem',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word'
                    }}
                  >
                    + Add Subject
                  </button>
                </div>
                {editErrors.subjects && (
                  <span className="error-text">{editErrors.subjects}</span>
                )}
                <div className="subjects-list" style={{ 
                  width: '100%', 
                  maxWidth: '100%', 
                  boxSizing: 'border-box',
                  overflowX: 'hidden',
                  padding: '1rem'
                }}>
                  {editForm.subjects.map((subject, index) => (
                    <div 
                      key={index} 
                      className="subject-item"
                      style={{
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: '1rem',
                        padding: '1rem',
                        overflowX: 'hidden'
                      }}
                    >
                      <select
                        value={subject}
                        onChange={(e) => handleEditSubjectChange(index, e.target.value)}
                        className={editErrors[`subject_${index}`] ? 'error' : ''}
                        style={{
                          width: '100%',
                          maxWidth: '100%',
                          boxSizing: 'border-box',
                          padding: '0.875rem 1rem',
                          fontSize: '1rem',
                          minWidth: 0,
                          flex: 'none',
                          borderRadius: '6px',
                          border: editErrors[`subject_${index}`] ? '1px solid #EF4444' : '1px solid #D1D5DB',
                          backgroundColor: '#FFFFFF'
                        }}
                      >
                        <option value="">-- Select Subject --</option>
                        {loadingSubjects ? (
                          <option disabled>Loading subjects...</option>
                        ) : availableSubjects.length > 0 ? (
                          availableSubjects.map((subjectName, idx) => (
                            <option key={idx} value={subjectName}>
                              {subjectName}
                            </option>
                          ))
                        ) : (
                          <option disabled>No subjects found. Add subjects in Curriculum Form first.</option>
                        )}
                      </select>
                      {editForm.subjects.length > 1 && (
                        <button
                          type="button"
                          className="remove-subject-btn"
                          onClick={() => removeEditSubject(index)}
                          title="Remove subject"
                          style={{
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            padding: '0.75rem 1rem',
                            fontSize: '1rem',
                            marginTop: '0.5rem'
                          }}
                        >
                          ×
                        </button>
                      )}
                      {editErrors[`subject_${index}`] && (
                        <span className="error-text">{editErrors[`subject_${index}`]}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-section">
                <h3>Emergency Contact</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Emergency Contact Name *</label>
                    <input
                      type="text"
                      name="emergency_contact_name"
                      value={editForm.emergency_contact_name}
                      onChange={handleEditInputChange}
                      className={editErrors.emergency_contact_name ? 'error' : ''}
                      placeholder="Emergency contact name"
                    />
                    {editErrors.emergency_contact_name && <span className="error-text">{editErrors.emergency_contact_name}</span>}
                  </div>
                  <div className="form-group">
                    <label>Emergency Phone *</label>
                    <div className="phone-input-wrapper">
                      <select
                        className="country-code-select"
                        value={editEmergencyPhoneCountryCode}
                        onChange={(e) => setEditEmergencyPhoneCountryCode(e.target.value)}
                      >
                        <option value="+1">🇺🇸 +1 (US/CA)</option>
                        <option value="+44">🇬🇧 +44 (UK)</option>
                        <option value="+91">🇮🇳 +91 (India)</option>
                        <option value="+92">🇵🇰 +92 (Pakistan)</option>
                        <option value="+971">🇦🇪 +971 (UAE)</option>
                        <option value="+966">🇸🇦 +966 (Saudi Arabia)</option>
                        <option value="+974">🇶🇦 +974 (Qatar)</option>
                        <option value="+965">🇰🇼 +965 (Kuwait)</option>
                        <option value="+973">🇧🇭 +973 (Bahrain)</option>
                        <option value="+968">🇴🇲 +968 (Oman)</option>
                        <option value="+961">🇱🇧 +961 (Lebanon)</option>
                        <option value="+962">🇯🇴 +962 (Jordan)</option>
                        <option value="+20">🇪🇬 +20 (Egypt)</option>
                        <option value="+27">🇿🇦 +27 (South Africa)</option>
                        <option value="+61">🇦🇺 +61 (Australia)</option>
                        <option value="+64">🇳🇿 +64 (New Zealand)</option>
                        <option value="+86">🇨🇳 +86 (China)</option>
                        <option value="+81">🇯🇵 +81 (Japan)</option>
                        <option value="+82">🇰🇷 +82 (South Korea)</option>
                        <option value="+65">🇸🇬 +65 (Singapore)</option>
                        <option value="+60">🇲🇾 +60 (Malaysia)</option>
                        <option value="+66">🇹🇭 +66 (Thailand)</option>
                        <option value="+62">🇮🇩 +62 (Indonesia)</option>
                        <option value="+63">🇵🇭 +63 (Philippines)</option>
                        <option value="+84">🇻🇳 +84 (Vietnam)</option>
                        <option value="+880">🇧🇩 +880 (Bangladesh)</option>
                        <option value="+94">🇱🇰 +94 (Sri Lanka)</option>
                        <option value="+977">🇳🇵 +977 (Nepal)</option>
                        <option value="+33">🇫🇷 +33 (France)</option>
                        <option value="+49">🇩🇪 +49 (Germany)</option>
                        <option value="+39">🇮🇹 +39 (Italy)</option>
                        <option value="+34">🇪🇸 +34 (Spain)</option>
                        <option value="+31">🇳🇱 +31 (Netherlands)</option>
                        <option value="+32">🇧🇪 +32 (Belgium)</option>
                        <option value="+41">🇨🇭 +41 (Switzerland)</option>
                        <option value="+43">🇦🇹 +43 (Austria)</option>
                        <option value="+46">🇸🇪 +46 (Sweden)</option>
                        <option value="+47">🇳🇴 +47 (Norway)</option>
                        <option value="+45">🇩🇰 +45 (Denmark)</option>
                        <option value="+358">🇫🇮 +358 (Finland)</option>
                        <option value="+7">🇷🇺 +7 (Russia)</option>
                        <option value="+90">🇹🇷 +90 (Turkey)</option>
                        <option value="+52">🇲🇽 +52 (Mexico)</option>
                        <option value="+55">🇧🇷 +55 (Brazil)</option>
                        <option value="+54">🇦🇷 +54 (Argentina)</option>
                        <option value="+57">🇨🇴 +57 (Colombia)</option>
                        <option value="+234">🇳🇬 +234 (Nigeria)</option>
                        <option value="+254">🇰🇪 +254 (Kenya)</option>
                        <option value="+212">🇲🇦 +212 (Morocco)</option>
                        <option value="+972">🇮🇱 +972 (Israel)</option>
                        <option value="+98">🇮🇷 +98 (Iran)</option>
                        <option value="+964">🇮🇶 +964 (Iraq)</option>
                        <option value="+967">🇾🇪 +967 (Yemen)</option>
                        <option value="+963">🇸🇾 +963 (Syria)</option>
                      </select>
                      <input
                        type="tel"
                        name="emergency_contact_phone"
                        value={editForm.emergency_contact_phone}
                        onChange={handleEditInputChange}
                        className={editErrors.emergency_contact_phone ? 'error' : ''}
                        placeholder="e.g. 300 1234567"
                      />
                    </div>
                    {editErrors.emergency_contact_phone && <span className="error-text">{editErrors.emergency_contact_phone}</span>}
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Password Change (Optional)</h3>
                <div className="password-change-info">
                  <p>Leave password fields empty if you don't want to change the password.</p>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="editPassword">New Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="editPassword"
                        name="password"
                        value={editForm.password}
                        onChange={handleEditInputChange}
                        className={editErrors.password ? 'error' : ''}
                        placeholder="Enter new password"
                      />
                      <span 
                        className="password-toggle-icon"
                        onClick={() => setShowPassword(!showPassword)}
                        title={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? '🙈' : '👁️'}
                      </span>
                    </div>
                    {editErrors.password && (
                      <span className="error-text">{editErrors.password}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="editConfirmPassword">Confirm Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="editConfirmPassword"
                        name="confirmPassword"
                        value={editForm.confirmPassword}
                        onChange={handleEditInputChange}
                        className={editErrors.confirmPassword ? 'error' : ''}
                        placeholder="Confirm new password"
                      />
                      <span 
                        className="password-toggle-icon"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        title={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        {showConfirmPassword ? '🙈' : '👁️'}
                      </span>
                    </div>
                    {editErrors.confirmPassword && (
                      <span className="error-text">{editErrors.confirmPassword}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={closeEditPopup}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={isSubmitting || uploadingPicture}>
                  {uploadingPicture ? 'Uploading Picture...' : isSubmitting ? 'Updating...' : 'Update Teacher'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Teacher Details Popup */}
      {viewingTeacher && (
        <div className="popup-overlay" onClick={closeViewPopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto', paddingBottom: '2rem' }}>
            <div className="popup-header" style={{ backgroundColor: '#FF8C42', borderRadius: '8px 8px 0 0', padding: '1rem 1.5rem' }}>
              <h3 style={{ color: '#FFFFFF', margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                Teacher Details: {viewingTeacher.first_name} {viewingTeacher.middle_name ? viewingTeacher.middle_name + ' ' : ''}{viewingTeacher.last_name}
              </h3>
              <button className="close-btn" onClick={closeViewPopup} style={{ color: '#FFFFFF', fontSize: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', padding: '0', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ×
              </button>
            </div>
            
            <div style={{ padding: '1.5rem', backgroundColor: '#F9FAFB' }}>
              {/* Profile Picture */}
              {viewingTeacherProfileUrl && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
                  <img 
                    src={viewingTeacherProfileUrl} 
                    alt={`${viewingTeacher.first_name} ${viewingTeacher.last_name}`}
                    style={{
                      width: '200px',
                      height: '200px',
                      objectFit: 'cover',
                      borderRadius: '50%',
                      border: '4px solid #FF8C42',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                    onError={async (e) => {
                      if (viewingTeacher.profile_picture_url) {
                        try {
                          const newUrl = await getValidFileUrl(viewingTeacher.profile_picture_url);
                          if (newUrl && newUrl !== e.target.src) {
                            e.target.src = newUrl;
                          } else {
                            e.target.style.display = 'none';
                          }
                        } catch (err) {
                          e.target.style.display = 'none';
                        }
                      }
                    }}
                  />
                </div>
              )}

              {/* Personal Information Card */}
              <div className="detail-section" style={{ marginBottom: '2rem' }}>
                <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                  Personal Information
                </h4>
                <div style={{
                  padding: '1.5rem',
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                  }}></div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>First Name</span>
                      <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                        {viewingTeacher.first_name || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Middle Name</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingTeacher.middle_name || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Last Name</span>
                      <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                        {viewingTeacher.last_name || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Date of Birth</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingTeacher.date_of_birth ? new Date(viewingTeacher.date_of_birth).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Email</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingTeacher.email ? (
                          <a href={`mailto:${viewingTeacher.email}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                            {viewingTeacher.email}
                          </a>
                        ) : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Phone</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingTeacher.phone ? (
                          <a href={`tel:${viewingTeacher.phone}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                            {viewingTeacher.phone}
                          </a>
                        ) : 'N/A'}
                      </div>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Address</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingTeacher.address || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Country</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingTeacher.country || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>City</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingTeacher.city || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Postal Code</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingTeacher.postal_code || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>National/Government ID</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingTeacher.national_id || 'N/A'}
                      </div>
                    </div>
                  <div>
                    <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Employee ID</span>
                    <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                      {viewingTeacher.employee_id || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Branch</span>
                    <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                      {viewingTeacher.branch_id ? (
                        (() => {
                          const branch = branches.find(b => b.id === viewingTeacher.branch_id);
                          return branch ? branch.branch_name : 'N/A';
                        })()
                      ) : 'All Branches'}
                    </div>
                  </div>
                  </div>
                </div>
              </div>

              {/* Professional Information Card */}
              <div className="detail-section" style={{ marginBottom: '2rem' }}>
                <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                  Professional Information
                </h4>
                <div style={{
                  padding: '1.5rem',
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                  }}></div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Qualification</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingTeacher.qualification || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Experience (Years)</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingTeacher.experience ? `${viewingTeacher.experience} years` : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Salary</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingTeacher.salary ? `$${parseFloat(viewingTeacher.salary).toLocaleString()}` : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Joining Date</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingTeacher.joining_date ? new Date(viewingTeacher.joining_date).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Status</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          background: viewingTeacher.is_active ? '#D1FAE5' : '#FEE2E2',
                          color: viewingTeacher.is_active ? '#065F46' : '#991B1B'
                        }}>
                          {viewingTeacher.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subjects Card */}
              {viewingTeacher.subjects && viewingTeacher.subjects.length > 0 && (
                <div className="detail-section" style={{ marginBottom: '2rem' }}>
                  <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                    Subjects
                  </h4>
                  <div style={{
                    padding: '1.5rem',
                    background: '#FFFFFF',
                    borderRadius: '12px',
                    border: '2px solid #E5E7EB',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                    }}></div>
                    
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.5rem' }}>
                      {viewingTeacher.subjects.map((subject, index) => (
                        <span key={index} style={{
                          padding: '0.5rem 1rem',
                          background: '#EFF6FF',
                          color: '#1E40AF',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          border: '1px solid #BFDBFE'
                        }}>
                          {subject}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Class Assignments Card */}
              {viewingTeacher.class_ids && viewingTeacher.class_ids.length > 0 && classes.length > 0 && (
                <div className="detail-section" style={{ marginBottom: '2rem' }}>
                  <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                    Class Assignments
                  </h4>
                  <div style={{
                    padding: '1.5rem',
                    background: '#FFFFFF',
                    borderRadius: '12px',
                    border: '2px solid #E5E7EB',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                    }}></div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '0.5rem' }}>
                      {viewingTeacher.class_ids.map((classId) => {
                        const classItem = classes.find(c => c.id === classId);
                        if (!classItem) return null;
                        const sections = viewingTeacher.class_sections?.[classId] || [];
                        return (
                          <div key={classId} style={{
                            padding: '1rem',
                            background: '#F9FAFB',
                            borderRadius: '8px',
                            border: '1px solid #E5E7EB'
                          }}>
                            <div style={{ fontWeight: '600', color: '#1F2937', marginBottom: '0.5rem', textAlign: 'center' }}>
                              {classItem.class_name}
                            </div>
                            {sections.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem', justifyContent: 'center' }}>
                                {sections.map((section, idx) => (
                                  <span key={idx} style={{
                                    padding: '0.25rem 0.75rem',
                                    background: '#FF8C42',
                                    color: '#FFFFFF',
                                    borderRadius: '6px',
                                    fontSize: '0.85rem',
                                    fontWeight: '500'
                                  }}>
                                    Section {section}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Emergency Contact Card */}
              {(viewingTeacher.emergency_contact_name || viewingTeacher.emergency_contact_phone) && (
                <div className="detail-section" style={{ marginBottom: '2rem' }}>
                  <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                    Emergency Contact
                  </h4>
                  <div style={{
                    padding: '1.5rem',
                    background: '#FFFFFF',
                    borderRadius: '12px',
                    border: '2px solid #E5E7EB',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                    }}></div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                      <div>
                        <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Contact Name</span>
                        <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                          {viewingTeacher.emergency_contact_name || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Contact Phone</span>
                        <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                          {viewingTeacher.emergency_contact_phone ? (
                            <a href={`tel:${viewingTeacher.emergency_contact_phone}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                              {viewingTeacher.emergency_contact_phone}
                            </a>
                          ) : 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Popup */}
      {showArchiveConfirmPopup && teacherToArchive && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={cancelArchiveTeacher}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(139, 92, 246, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>📦</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Archive Teacher?
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '1.5rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              Are you sure you want to archive <strong>{teacherToArchive.first_name} {teacherToArchive.last_name}</strong>?
              <br /><br />
              The teacher will be marked as inactive and hidden from the active teachers list. They can be restored later if needed.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
              <button onClick={cancelArchiveTeacher} disabled={isSubmitting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: '2px solid #E5E7EB', background: '#FFFFFF', color: '#6B7280', fontSize: '1rem', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isSubmitting ? 0.5 : 1 }}>
                Cancel
              </button>
              <button onClick={confirmArchiveTeacher} disabled={isSubmitting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isSubmitting ? 0.5 : 1, boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)' }}>
                {isSubmitting ? 'Archiving...' : 'Archive Teacher'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Success Popup */}
      {showArchiveSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowArchiveSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>✓</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Teacher Archived Successfully! 🎉
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '2rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              <strong>{archivedTeacherName}</strong> has been archived successfully.
              <br />
              They are now marked as inactive.
            </p>
            <button onClick={() => setShowArchiveSuccessPopup(false)} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)' }}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Restore Confirmation Popup */}
      {showRestoreConfirmPopup && teacherToRestore && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={cancelRestoreTeacher}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>♻️</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Restore Teacher?
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '1.5rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              Are you sure you want to restore <strong>{teacherToRestore.first_name} {teacherToRestore.last_name}</strong>?
              <br /><br />
              The teacher will be marked as active and will appear in the active teachers list.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
              <button onClick={cancelRestoreTeacher} disabled={isSubmitting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: '2px solid #E5E7EB', background: '#FFFFFF', color: '#6B7280', fontSize: '1rem', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isSubmitting ? 0.5 : 1 }}>
                Cancel
              </button>
              <button onClick={confirmRestoreTeacher} disabled={isSubmitting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isSubmitting ? 0.5 : 1, boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)' }}>
                {isSubmitting ? 'Restoring...' : 'Restore Teacher'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Success Popup */}
      {showRestoreSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowRestoreSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>✓</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Teacher Restored Successfully! 🎉
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '2rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              <strong>{restoredTeacherName}</strong> has been restored successfully.
              <br />
              The teacher is now marked as active.
            </p>
            <button onClick={() => setShowRestoreSuccessPopup(false)} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)' }}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Edit Teacher Success Popup */}
      {showEditSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => {
            setShowEditSuccessPopup(false);
            setUpdatedTeacherName('');
          }}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative Background Circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Success Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>✓</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Teacher Updated Successfully! 🎉
            </h2>

            {/* Subtitle */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '2rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              <strong>{updatedTeacherName}</strong> has been updated successfully.
              <br />
              All changes have been saved.
            </p>

            {/* Close Button */}
            <button
              onClick={() => {
                setShowEditSuccessPopup(false);
                setUpdatedTeacherName('');
              }}
              style={{
                padding: '0.75rem 2rem',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: '#FFFFFF',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                position: 'relative',
                zIndex: 1
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'linear-gradient(135deg, #10B981 0%, #059669 100%)';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirmPopup && teacherToDelete && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={cancelDeleteTeacher}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Delete Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(239, 68, 68, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>🗑️</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Delete Teacher?
            </h2>

            {/* Message */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '1.5rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              Are you sure you want to <strong style={{ color: '#EF4444' }}>PERMANENTLY DELETE</strong> <strong>{teacherToDelete.name}</strong>?
              <br /><br />
              This will:
              <br />
              • Remove the teacher from the database completely
              <br />
              • Delete their login account (if exists)
              <br />
              • Remove all attendance records
              <br />
              • Remove all class schedule records
              <br />
              • Remove all class assignments
              <br />
              <strong style={{ color: '#EF4444' }}>This action CANNOT be undone!</strong>
            </p>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <button
                onClick={cancelDeleteTeacher}
                disabled={isSubmitting}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  background: '#FFFFFF',
                  color: '#6B7280',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.background = '#F9FAFB';
                    e.target.style.borderColor = '#D1D5DB';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#FFFFFF';
                  e.target.style.borderColor = '#E5E7EB';
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteTeacher}
                disabled={isSubmitting}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  color: '#FFFFFF',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? 0.5 : 1,
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
                }}
              >
                {isSubmitting ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Success Popup */}
      {showDeleteSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowDeleteSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Success Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>✓</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Teacher Deleted Successfully! 🎉
            </h2>

            {/* Message */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '2rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              <strong>{deletedTeacherName}</strong> has been permanently deleted from the database.
            </p>

            {/* Button */}
            <button
              onClick={() => setShowDeleteSuccessPopup(false)}
              style={{
                padding: '0.75rem 2rem',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: '#FFFFFF',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default Teachers;
